<script setup lang="ts">
import { computed } from 'vue';
import CheckCircleFilledSVG from '@shared/assets/icon/check-circle-filled.svg';
import DangerSVG from '@shared/assets/icon/danger.svg';

const props = defineProps<{
  title?: string;
  progress: number;
  progressText?: string;
  success?: boolean;
  failure?: boolean;
  doneRemarks?: string;
  titleTextAlign?: 'left' | 'center';
}>();

const isDone = computed(() => props.success || props.failure);
</script>

<template>
  <div
    class="component-container"
    :class="{ 'component-container--success': success, 'component-container--failure': failure }"
  >
    <div v-if="title" class="title-container">
      <h4 class="title" :class="{ 'title--left': titleTextAlign === 'left' }">{{ title }}</h4>
      <p v-if="isDone && doneRemarks" class="done-remarks">{{ doneRemarks }}</p>
    </div>
    <template v-if="!isDone">
      <div class="progress-bar">
        <div class="progress-bar-fill" :style="{ width: `${progress}%` }"></div>
      </div>
      <div v-if="progressText" class="description-container">
        <span class="text">{{ progressText }}</span>
      </div>
    </template>
    <template v-else>
      <div class="result-icon-container">
        <CheckCircleFilledSVG v-if="success" class="success"/>
        <DangerSVG v-if="failure" class="failed"/>
      </div>
    </template>
  </div>
</template>

<style lang="less" scoped>
.component-container {
  display: flex;
  flex-direction: column;
  align-items: start;
  justify-content: center;
  padding: 1.2rem;
  border: 1px solid @color-mid-tone;
  border-radius: 1rem;
  background-color: #fff;
  min-height: 6rem;
}

.component-container--success,
.component-container--failure {
  position: relative;
  .title-container {
    margin-bottom: 0;
  }
}

.component-container--success {
  background-color: @color-mid-tone;
}

.title-container {
  font-weight: @font-weight-regular;
  font-size: 1.2rem;
  margin-bottom: 0.5rem;

  .title {
    margin-bottom: 0;
  }

  .title--left {
    text-align: left;
  }

  .done-remarks {
    font-size: 0.8rem;
    font-weight: @font-weight-regular;
    color: @color-primary;
    margin: 0;
    text-align: left;
  }
}

.progress-bar {
  width: 100%;
  height: 10px;
  background-color: #fff;
  border-radius: 1rem;
  border: 1px solid @color-mid-tone;
  overflow: hidden;
  position: relative;

  .progress-bar-fill {
    height: 100%;
    background-color: @color-primary;
    border-radius: 1rem;
    transition: width 0.3s ease;
    animation: pulseProgressBar 1.5s ease-in-out infinite;
  }
}

.description-container {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-top: 0.5rem;
  padding-left: 0.5rem;

  .text {
    font-weight: @font-weight-regular;
    font-size: 0.8rem;
  }
}

.result-icon-container {
  position: absolute;
  right: 20px;

  .success {
    color: @color-primary;
  }

  .failed {
    color: @color-warning;
  }
}

@keyframes pulseProgressBar {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.7;
  }
}
</style>
